import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient, type MutationCacheNotifyEvent } from '@tanstack/react-query';
import { useAssetOverview } from '../context/AssetOverviewContext';
import AssetManagerService from '../services/AssetManager/assetManager.service';
import { buildConditionsCql, buildContainsCql, joinCql } from '../shared/utils/conditionsCql';
import { ASSET_HIGHLIGHT_COLOR, NEW_LAYER_NOTICE } from '../views/InfrastructureManager/AssetOverview/constants';
import { MutationKeys } from '../enums/mutationKeys';
import { QueryKeys } from '../enums/queryKeys';
import type { CommonMapModule } from '../context/CommonMapModuleContext';
import type { AssetManagerLayer } from '../types/InfrastructureManagerTypes';
import type { MapEngine } from '../types/CommonMapTypes';

const NO_LAYERS: AssetManagerLayer[] = [];
const NO_LAYER_IDS: number[] = [];

const isCreateFeatureSuccess = (event: MutationCacheNotifyEvent) =>
  event.type === 'updated' && event.action.type === 'success' && event.mutation.options.mutationKey?.[0] === MutationKeys.CREATE_FEATURE;

// Shapes the shared map to the Asset Overview dataset, so the map always shows what the list lists:
// the configured layers narrowed by the search, or the active statistic's layer and conditions.
export const useAssetMapSync = (onEngineChange: (engine: MapEngine | null) => void): CommonMapModule => {
  const queryClient = useQueryClient();
  const { config, search, activeStatistic, toggleSelect } = useAssetOverview();
  const { getMetaLayers } = AssetManagerService();
  const { data: metaLayers = NO_LAYERS } = useQuery(getMetaLayers());

  const rows = config?.rows;

  // The default extent fits the configured layers only.
  const extentLayerIds = useMemo(() => (rows?.length ? rows.map(({ layerId }) => layerId) : undefined), [rows]);

  const scope = useMemo((): Pick<CommonMapModule, 'layerIds' | 'filterByLayerId'> => {
    const term = search.trim();
    if (activeStatistic) {
      const { layerId, conditions } = activeStatistic;
      // BE: a layer outside the list config has no display field; its key field is assumed for the search.
      const displayField = rows?.find((row) => row.layerId === layerId)?.displayField ?? metaLayers.find(({ id }) => id === layerId)?.keyField;
      const cql = joinCql(buildConditionsCql(conditions), term && displayField ? buildContainsCql(displayField, term) : '');
      return { layerIds: [layerId], filterByLayerId: cql ? new Map([[layerId, cql]]) : undefined };
    }
    // Not configured yet: no layers are drawn or listed.
    if (!rows?.length) return { layerIds: NO_LAYER_IDS };
    return {
      layerIds: rows.map(({ layerId }) => layerId),
      filterByLayerId: term ? new Map(rows.map(({ layerId, displayField }) => [layerId, buildContainsCql(displayField, term)])) : undefined,
    };
  }, [rows, search, activeStatistic, metaLayers]);

  // A pick selects by the layer's key field; picking the selected asset again deselects it.
  const handleFeaturePick = useCallback(
    (layerId: number, attributes: Record<string, unknown>) => {
      const keyField = metaLayers.find(({ id }) => id === layerId)?.keyField;
      const keyValue = keyField ? attributes[keyField] : undefined;
      if (keyValue === undefined || keyValue === null) return;
      const displayField = rows?.find((row) => row.layerId === layerId)?.displayField;
      const displayValue = displayField ? attributes[displayField] : undefined;
      toggleSelect({ layerId, keyValue: String(keyValue), displayValue: String(displayValue ?? keyValue) });
    },
    [metaLayers, rows, toggleSelect],
  );

  // A created feature can join the dataset and change computed values, so both are refetched.
  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((event) => {
        if (!isCreateFeatureSuccess(event)) return;
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ASSET_LIST_PAGE] });
        void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_ASSET_STATISTICS] });
      }),
    [queryClient],
  );

  // The assets the active statistic counts; with no conditions that is the whole layer.
  const highlight = useMemo(
    () =>
      activeStatistic
        ? { layerId: activeStatistic.layerId, cql: buildConditionsCql(activeStatistic.conditions) || 'INCLUDE', color: ASSET_HIGHLIGHT_COLOR }
        : undefined,
    [activeStatistic],
  );

  return useMemo(
    () => ({ ...scope, extentLayerIds, onFeaturePick: handleFeaturePick, highlight, newLayerNotice: NEW_LAYER_NOTICE, onEngineChange }),
    [scope, extentLayerIds, handleFeaturePick, highlight, onEngineChange],
  );
};
